import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  LEARN_CARDS, WITNESS_ITEMS, PRACTICE_ITEMS, SEVEN_UNDERSTANDINGS, CAPSTONE_KEY,
  type AttestationRow,
} from "./pathContent";
import { SUCCESSION_STATUS_LABEL } from "@/lib/succession/stages";

interface Successor {
  id: string;
  successor_name: string | null;
  officer_role_to_succeed: string | null;
  status: string;
  invited_email: string;
  accepted_at: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  associationId: string;
  associationName: string;
  onCompleted?: () => void;
}

export function StewardReviewPanel({ open, onOpenChange, associationId, associationName, onCompleted }: Props) {
  const [loading, setLoading] = useState(true);
  const [successors, setSuccessors] = useState<Successor[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rows, setRows] = useState<AttestationRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  async function loadSuccessors() {
    setLoading(true);
    const { data, error } = await supabase
      .from("succession_successors")
      .select("id, successor_name, officer_role_to_succeed, status, invited_email, accepted_at")
      .eq("association_id", associationId)
      .in("status", ["active", "path_complete", "commissioned"])
      .order("created_at", { ascending: false });
    if (error) toast.error("Could not load successors.");
    const list = (data ?? []) as Successor[];
    setSuccessors(list);
    if (list.length && !selectedId) setSelectedId(list[0].id);
    setLoading(false);
  }

  async function loadAttestations(sid: string) {
    const { data } = await supabase
      .from("succession_attestations")
      .select("*")
      .eq("successor_id", sid);
    setRows((data ?? []) as AttestationRow[]);
  }

  useEffect(() => { if (open) loadSuccessors(); /* eslint-disable-next-line */ }, [open, associationId]);
  useEffect(() => { if (selectedId) loadAttestations(selectedId); }, [selectedId]);

  const selected = successors.find((s) => s.id === selectedId) || null;
  const byKey = new Map(rows.map((r) => [`${r.stage}:${r.item_key}`, r]));

  async function toggleChair(row: AttestationRow, confirm: boolean) {
    setBusy(row.id);
    try {
      const uid = (await supabase.auth.getUser()).data.user?.id ?? null;
      const { error } = await supabase
        .from("succession_attestations")
        .update({
          chair_confirmed_at: confirm ? new Date().toISOString() : null,
          chair_confirmed_by: confirm ? uid : null,
        })
        .eq("id", row.id);
      if (error) throw error;
      if (selectedId) await loadAttestations(selectedId);
      await maybeMarkPathComplete();
    } catch (e: any) {
      toast.error("Could not update. Please try again.");
    } finally {
      setBusy(null);
    }
  }

  async function maybeMarkPathComplete() {
    if (!selected) return;
    // Re-fetch fresh attestation state.
    const { data } = await supabase
      .from("succession_attestations")
      .select("*")
      .eq("successor_id", selected.id);
    const fresh = (data ?? []) as AttestationRow[];
    const map = new Map(fresh.map((r) => [`${r.stage}:${r.item_key}`, r]));
    const learnOk = LEARN_CARDS.every((c) => map.get(`learn:${c.key}`)?.successor_confirmed_at);
    const witnessOk = WITNESS_ITEMS.every((c) => !!map.get(`witness:${c.key}`)?.chair_confirmed_at);
    const practiceOk = PRACTICE_ITEMS.every((c) => !!map.get(`practice:${c.key}`)?.chair_confirmed_at);
    const capstoneOk = !!map.get(`learn:${CAPSTONE_KEY}`)?.successor_confirmed_at;
    if (learnOk && witnessOk && practiceOk && capstoneOk && selected.status !== "path_complete" && selected.status !== "commissioned") {
      const { error } = await supabase
        .from("succession_successors")
        .update({ status: "path_complete" })
        .eq("id", selected.id);
      if (!error) {
        toast.success("The path is complete. Well done.", { duration: 6000 });
        await loadSuccessors();
        onCompleted?.();
      }
    }
  }

  const capstoneRow = byKey.get(`learn:${CAPSTONE_KEY}`);
  const capstoneAcks = parseCapstone(capstoneRow?.successor_response ?? null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Read your successor's path</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : successors.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No successor has said yes yet. When your invitation is accepted, their path will show up here.
          </div>
        ) : (
          <div className="space-y-6">
            {successors.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {successors.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedId(s.id)}
                    className={`text-sm rounded-md border px-3 py-1.5 ${
                      selectedId === s.id ? "border-primary bg-primary/5" : "hover:border-primary/40"
                    }`}
                  >
                    {s.successor_name || s.invited_email}
                  </button>
                ))}
              </div>
            )}

            {selected && (
              <div className="space-y-6">
                <Card className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{selected.successor_name || selected.invited_email}</p>
                      <p className="text-xs text-muted-foreground">
                        Getting ready to be {selected.officer_role_to_succeed || "steward"} at {associationName}
                      </p>
                    </div>
                    <span className={`text-xs rounded-full px-3 py-1 ${
                      selected.status === "path_complete" || selected.status === "commissioned"
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground"
                    }`}>
                      {SUCCESSION_STATUS_LABEL[selected.status] ?? "In progress"}
                    </span>
                  </div>
                </Card>

                <Section title="Chapter one. Learn.">
                  <p className="text-xs text-muted-foreground">Your successor's own words on each short read. Read them. Nothing to sign here.</p>
                  {LEARN_CARDS.map((c) => {
                    const r = byKey.get(`learn:${c.key}`);
                    return (
                      <ReadOnlyItem key={c.key} title={c.title} response={r?.successor_response} confirmedAt={r?.successor_confirmed_at} />
                    );
                  })}
                </Section>

                <Section title="Chapter two. Witness.">
                  <p className="text-xs text-muted-foreground">You did these together. Mark each one done when it is done.</p>
                  {WITNESS_ITEMS.map((c) => {
                    const r = byKey.get(`witness:${c.key}`);
                    return (
                      <ChairItem
                        key={c.key}
                        title={c.title}
                        response={r?.successor_response}
                        row={r}
                        onToggle={(row, v) => toggleChair(row, v)}
                        busyId={busy}
                      />
                    );
                  })}
                </Section>

                <Section title="Chapter three. Practice.">
                  <p className="text-xs text-muted-foreground">Mark each practice item done when you have done it together.</p>
                  {PRACTICE_ITEMS.map((c) => {
                    const r = byKey.get(`practice:${c.key}`);
                    return (
                      <ChairItem
                        key={c.key}
                        title={c.title}
                        response={r?.successor_response}
                        row={r}
                        onToggle={(row, v) => toggleChair(row, v)}
                        busyId={busy}
                      />
                    );
                  })}
                </Section>

                <Section title="Capstone. The Seven Promises.">
                  {!capstoneRow?.successor_confirmed_at ? (
                    <p className="text-sm text-muted-foreground">Your successor has not said yes to the Seven Promises yet.</p>
                  ) : (
                    <Card className="p-4 space-y-2 border-primary/40 bg-primary/5">
                      <div className="flex items-center gap-2 text-primary">
                        <Sparkles className="h-4 w-4" />
                        <span className="text-sm font-medium">Said yes to all seven</span>
                      </div>
                      <ul className="text-xs space-y-1 text-muted-foreground pl-1">
                        {SEVEN_UNDERSTANDINGS.map((_, i) => (
                          <li key={i} className="flex items-start gap-2">
                            {capstoneAcks.includes(i) ? <Check className="h-3 w-3 mt-0.5 text-primary" /> : <span className="w-3" />}
                            <span>Promise {i + 1}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}
                </Section>

                {selected.status === "path_complete" && (
                  <Card className="p-6 text-center space-y-2 border-primary/50 bg-primary/5">
                    <Sparkles className="h-8 w-8 text-primary mx-auto" />
                    <h3 className="font-serif text-lg">The path is complete.</h3>
                    <p className="text-sm text-muted-foreground">
                      Your successor has walked all three chapters and said yes to the Seven Promises. The next stages will open on your dashboard.
                    </p>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h3 className="font-serif text-lg">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ReadOnlyItem({ title, response, confirmedAt }: { title: string; response?: string | null; confirmedAt?: string | null }) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="font-medium text-sm">{title}</p>
          {response ? (
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{response}</p>
          ) : (
            <p className="text-xs text-muted-foreground/70 mt-1">Not answered yet.</p>
          )}
        </div>
        {confirmedAt && <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />}
      </div>
    </Card>
  );
}

function ChairItem({ title, response, row, onToggle, busyId }: {
  title: string;
  response?: string | null;
  row?: AttestationRow;
  onToggle: (row: AttestationRow, v: boolean) => void;
  busyId: string | null;
}) {
  const confirmed = !!row?.chair_confirmed_at;
  const canConfirm = !!row?.successor_confirmed_at;
  return (
    <Card className={`p-4 ${confirmed ? "border-primary/40 bg-primary/5" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="font-medium text-sm">{title}</p>
          {response ? (
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{response}</p>
          ) : (
            <p className="text-xs text-muted-foreground/70 mt-1">Your successor has not written a note yet.</p>
          )}
        </div>
        {row && (
          <Button
            size="sm"
            variant={confirmed ? "outline" : "default"}
            disabled={!canConfirm || busyId === row.id}
            onClick={() => onToggle(row, !confirmed)}
            className="shrink-0"
          >
            {busyId === row.id && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
            {confirmed ? "Done" : "Mark done"}
          </Button>
        )}
      </div>
    </Card>
  );
}

function parseCapstone(s: string | null): number[] {
  if (!s) return [];
  try {
    const v = JSON.parse(s);
    if (Array.isArray(v?.acknowledged_indexes)) return v.acknowledged_indexes;
  } catch {}
  return [];
}
